import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

const TURNSTILE_SCRIPT_ID = "nouvac-turnstile-script";
const TURNSTILE_SCRIPT_URL =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
const TURNSTILE_ACTION = "quote_request_submit";

let turnstileScriptPromise;

export function loadTurnstileApi() {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (turnstileScriptPromise) return turnstileScriptPromise;

  turnstileScriptPromise = new Promise((resolve, reject) => {
    const existingScript = document.getElementById(TURNSTILE_SCRIPT_ID);
    const script = existingScript || document.createElement("script");

    const handleLoad = () => {
      if (window.turnstile) resolve(window.turnstile);
      else reject(new Error("turnstile_unavailable"));
    };
    const handleError = () => reject(new Error("turnstile_unavailable"));

    script.addEventListener("load", handleLoad, { once: true });
    script.addEventListener("error", handleError, { once: true });

    if (!existingScript) {
      script.id = TURNSTILE_SCRIPT_ID;
      script.src = TURNSTILE_SCRIPT_URL;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  });

  return turnstileScriptPromise;
}

const TurnstileField = forwardRef(function TurnstileField(
  {
    siteKey,
    onTokenChange,
    loader = loadTurnstileApi,
    disabled = false,
  },
  forwardedRef
) {
  const containerRef = useRef(null);
  const apiRef = useRef(null);
  const widgetIdRef = useRef(null);
  const onTokenChangeRef = useRef(onTokenChange);
  const [status, setStatus] = useState(siteKey ? "loading" : "configuration_error");

  onTokenChangeRef.current = onTokenChange;

  useImperativeHandle(
    forwardedRef,
    () => ({
      reset() {
        onTokenChangeRef.current("");
        if (apiRef.current && widgetIdRef.current !== null) {
          try {
            apiRef.current.reset(widgetIdRef.current);
          } catch {
            setStatus("unavailable");
          }
        }
      },
    }),
    []
  );

  useEffect(() => {
    if (!siteKey) {
      setStatus("configuration_error");
      onTokenChangeRef.current("");
      return undefined;
    }

    let cancelled = false;
    setStatus("loading");

    Promise.resolve()
      .then(() => loader())
      .then((turnstileApi) => {
        if (
          cancelled ||
          !turnstileApi ||
          typeof turnstileApi.render !== "function" ||
          !containerRef.current ||
          widgetIdRef.current !== null
        ) {
          return;
        }

        apiRef.current = turnstileApi;
        widgetIdRef.current = turnstileApi.render(containerRef.current, {
          sitekey: siteKey,
          action: TURNSTILE_ACTION,
          callback: (token) => {
            if (!cancelled) {
              setStatus("ready");
              onTokenChangeRef.current(token);
            }
          },
          "expired-callback": () => {
            if (!cancelled) {
              setStatus("expired");
              onTokenChangeRef.current("");
            }
          },
          "error-callback": () => {
            if (!cancelled) {
              setStatus("unavailable");
              onTokenChangeRef.current("");
            }
          },
        });
        setStatus("ready");
      })
      .catch(() => {
        if (!cancelled) {
          setStatus("unavailable");
          onTokenChangeRef.current("");
        }
      });

    return () => {
      cancelled = true;
      onTokenChangeRef.current("");
      if (
        apiRef.current &&
        widgetIdRef.current !== null &&
        typeof apiRef.current.remove === "function"
      ) {
        try {
          apiRef.current.remove(widgetIdRef.current);
        } catch {
          // The provider widget may already have removed itself.
        }
      }
      widgetIdRef.current = null;
      apiRef.current = null;
    };
  }, [loader, siteKey]);

  const message =
    status === "configuration_error"
      ? "La vérification de sécurité n’est pas configurée. Veuillez réessayer ultérieurement."
      : status === "unavailable"
        ? "La vérification de sécurité est momentanément indisponible. Réessayez dans quelques instants."
        : status === "expired"
          ? "La vérification de sécurité a expiré. Veuillez la renouveler."
          : "";

  return (
    <div className="turnstile-field">
      <div
        ref={containerRef}
        className="turnstile-container"
        aria-disabled={disabled}
      />
      {message ? (
        <p className="turnstile-message" role="status">
          {message}
        </p>
      ) : null}
    </div>
  );
});

export default TurnstileField;
